import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    Button,
    Stack,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Pagination,
    InputAdornment,
    Switch,
    FormControlLabel,
    Grid,
} from '@mui/material';
import { Plus, SquarePen, Trash2, Search, Eye, EyeOff } from 'lucide-react';
import {
    getAllAdminUsers,
    createAdminUser,
    updateAdminUser,
    deleteAdminUser,
} from '../../Services/AdminUsers/AdminUsersApis';
import DeleteModal from '../../Components/Modals/DeleteModal';
import PermissionWrapper from '../../Components/Auth/PermissionWrapper';
import { getAllRoles } from '../../Services/RolesPermissions/RolesPermissionsApi';
import { useGlobalSnackbar } from '../../Components/Common/GlobalSanckbar';

const AdminUsers = () => {
    const { showSnackbar } = useGlobalSnackbar();
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const rowsPerPage = 10;

    // Modal states
    const [openModal, setOpenModal] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [currentUser, setCurrentUser] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    // Delete modal
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    // Form data
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        roleId: '',
        isActive: true
    });
    const [showPassword, setShowPassword] = useState(false);

    // Audit Info for edit mode
    const [auditInfo, setAuditInfo] = useState({
        cuName: '',
        cuType: '',
        cuId: '',
        uuName: '',
        uuType: '',
        uuId: '',
        createdAt: '',
        updatedAt: ''
    });

    // Fetch users
    const fetchUsers = useCallback(async (searchQuery = '', currentPage = 1) => {
        try {
            setLoading(true);
            const response = await getAllAdminUsers(currentPage, rowsPerPage, searchQuery);
            if (response && response.success) {
                setUsers(response.data || []);
                setTotalItems(response.totalRecords || response.totalItems || 0);
                setTotalPages(response.totalPages || Math.ceil((response.totalRecords || response.totalItems || 0) / rowsPerPage) || 1);
                setError(null);
            } else {
                setUsers([]);
                setError("Failed to load admin users data.");
            }
        } catch (err) {
            console.error("Error fetching admin users:", err);
            setError("Failed to fetch admin users. Please try again later.");
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    // Fetch roles
    const fetchRoles = useCallback(async () => {
        try {
            const response = await getAllRoles();
            if (response && response.success) {
                setRoles(response.data || []);
            }
        } catch (err) {
            console.error("Error fetching roles:", err);
        }
    }, []);

    useEffect(() => {
        fetchUsers();
        fetchRoles();
    }, [fetchUsers, fetchRoles]);

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchTerm(value);
        setPage(1);
        // Debounce search
        const timeoutId = setTimeout(() => {
            fetchUsers(value, 1);
        }, 500);
        return () => clearTimeout(timeoutId);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        fetchUsers(searchTerm, newPage);
    };

    const handleInputChange = (e) => {
        let { name, value } = e.target;

        if (name === 'name') {
            value = value.replace(/[^a-zA-Z\s]/g, '').slice(0, 100);
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        if (fieldErrors[name]) {
            setFieldErrors(prev => {
                const updated = { ...prev };
                delete updated[name];
                return updated;
            });
        }
    };

    const handleOpenModal = async (user = null) => {
        setOpenModal(true);
        setError(null);
        setModalError(null);
        setFieldErrors({});

        if (user) {
            setIsEdit(true);
            setCurrentUser(user);
            setAuditInfo({
                cuName: user.cuName || '',
                cuType: user.cuType || '',
                cuId: user.cuId || '',
                uuName: user.uuName || '',
                uuType: user.uuType || '',
                uuId: user.uuId || '',
                createdAt: user.createdAt || '',
                updatedAt: user.updatedAt || ''
            });
            setFormData({
                name: user.name || '',
                email: user.email || '',
                password: '', // Don't show existing password
                roleId: user.roleId || user.role?.id || '',
                isActive: user.isActive !== undefined ? user.isActive : true
            });
        } else {
            setIsEdit(false);
            setCurrentUser(null);
            setAuditInfo({
                cuName: '',
                cuType: '',
                cuId: '',
                uuName: '',
                uuType: '',
                uuId: '',
                createdAt: '',
                updatedAt: ''
            });
            setFormData({
                name: '',
                email: '',
                password: '',
                roleId: '',
                isActive: true
            });
        }
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setIsEdit(false);
        setCurrentUser(null);
        setAuditInfo({
            cuName: '',
            cuType: '',
            cuId: '',
            uuName: '',
            uuType: '',
            uuId: '',
            createdAt: '',
            updatedAt: ''
        });
        setFormData({
            name: '',
            email: '',
            password: '',
            roleId: '',
            isActive: true
        });
        setError(null);
        setModalError(null);
        setFieldErrors({});
        setModalLoading(false);
    };

    const handleSubmit = async () => {
        setFieldErrors({});
        setModalError(null);

        const newFieldErrors = {};
        if (!formData.name || !formData.name.trim()) newFieldErrors.name = "Name is required";
        if (!formData.email) newFieldErrors.email = "Email is required";
        if (!isEdit && !formData.password) newFieldErrors.password = "Password is required";
        if (!formData.roleId) newFieldErrors.roleId = "Role is required";

        if (Object.keys(newFieldErrors).length > 0) {
            setFieldErrors(newFieldErrors);
            setModalError("Please fill in all required fields.");
            return;
        }

        setSubmitting(true);
        setError(null);

        const submitData = {
            name: formData.name,
            email: formData.email,
            roleId: parseInt(formData.roleId),
            isActive: formData.isActive
        };

        if (formData.password) {
            submitData.password = formData.password;
        }

        try {
            let res;
            if (isEdit) {
                res = await updateAdminUser(currentUser.id, submitData);
            } else {
                res = await createAdminUser(submitData);
            }

            if (res && res.success) {
                showSnackbar(res.message || `Admin user ${isEdit ? 'updated' : 'created'} successfully!`, "success");
                handleCloseModal();
                fetchUsers(searchTerm, page);
            } else {
                setModalError(res?.message || `Failed to ${isEdit ? 'update' : 'create'} admin user`);
            }
        } catch (err) {
            const responseData = err.response?.data;
            const errorsObj = {};

            if (responseData?.details && Array.isArray(responseData.details) && responseData.details.length > 0) {
                responseData.details.forEach(item => {
                    if (item.field && item.message) {
                        errorsObj[item.field] = item.message;
                    }
                });
                setFieldErrors(errorsObj);
                if (Object.keys(errorsObj).length === 0) {
                    setModalError(responseData.message || "Validation failed");
                } else if (responseData.message && responseData.message !== "Validation failed") {
                    setModalError(responseData.message);
                }
            } else if (responseData?.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0) {
                setModalError(responseData.errors.join(', '));
                responseData.errors.forEach(errStr => {
                    if (errStr.toLowerCase().includes('email')) {
                        errorsObj['email'] = errStr;
                    } else if (errStr.toLowerCase().includes('password')) {
                        errorsObj['password'] = errStr;
                    } else if (errStr.toLowerCase().includes('name')) {
                        errorsObj['name'] = errStr;
                    } else if (errStr.toLowerCase().includes('role')) {
                        errorsObj['roleId'] = errStr;
                    }
                });
                if (Object.keys(errorsObj).length > 0) {
                    setFieldErrors(errorsObj);
                }
            } else {
                const errMsg = responseData?.message || err.message || `Failed to ${isEdit ? 'update' : 'create'} admin user`;
                setModalError(errMsg);
                if (errMsg.toLowerCase().includes('email')) {
                    setFieldErrors(prev => ({ ...prev, email: errMsg }));
                }
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedUserId(id);
        setDeleteError(null);
        setDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (selectedUserId) {
            try {
                setIsDeleting(true);
                setDeleteError(null);
                const response = await deleteAdminUser(selectedUserId);
                if (response && response.success) {
                    showSnackbar(response.message || "Admin user deleted successfully!", "success");
                    setDeleteModalOpen(false);
                    setSelectedUserId(null);
                    fetchUsers(searchTerm, page);
                } else {
                    setDeleteError(response?.message || "Failed to delete admin user");
                }
            } catch (err) {
                console.error("Delete Error:", err);
                const errorMessage = err.response?.data?.message || err.message || "Failed to delete admin user";
                setDeleteError(errorMessage);
            } finally {
                setIsDeleting(false);
            }
        }
    };

    const getRoleName = (roleId) => {
        const role = roles.find(r => r.id === roleId);
        return role ? role.name : 'N/A';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '-';
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    return (
        <PermissionWrapper permission="view">
            <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>


                {/* Header */}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}
                >
                    <Typography sx={{ color: '#222', fontWeight: 500, fontSize: { xs: '1.75rem', md: '1.8rem' }, }}> Admin Users Management </Typography>
                    {/* Search */}
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <TextField
                            size="small" placeholder="Search by name or email..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            sx={{
                                bgcolor: '#fff', width: { xs: '100%', sm: '260px' },
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    height: '40px',
                                    fontSize: '0.85rem',
                                    '& fieldset': { borderColor: '#E0E0E0' },
                                    '&:hover fieldset': { borderColor: '#BDBDBD' },
                                    '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start" sx={{ mr: 0.5 }}>
                                        <Search size={16} color="#666" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <PermissionWrapper permission="create" mode="disable">
                            <Button onClick={() => handleOpenModal()}
                                disableElevation disableRipple disableFocusRipple disableTouchRipple
                                variant="contained" startIcon={<Plus size={18} />}
                                sx={{
                                    bgcolor: '#FFBD65',
                                    px: 2.5,
                                    height: '40px',
                                    color: '#fff',
                                    borderRadius: '6px',
                                    textTransform: 'none',
                                    fontSize: '0.85rem'
                                }}
                            >
                                Add Admin User
                            </Button>
                        </PermissionWrapper>
                    </Box>
                </Stack>



                {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                {/* Table */}
                <Paper sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", overflow: "hidden" }} elevation={0}>
                    <TableContainer>
                        <Table sx={{ width: '100%' }}>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#F8FAFC', borderBottom: '2px solid #EEF2F6' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1, whiteSpace: 'nowrap', textAlign: 'center', width: '4%' }}>S.No</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '14%' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '17%' }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '10%' }}>Role</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '8%' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '11%' }}>Created By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '10%' }}>Created At</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '11%' }}>Updated By</TableCell>
                                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1.25, whiteSpace: 'nowrap', width: '10%' }}>Updated At</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: '#475569', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.6px', py: 1.5, px: 1, whiteSpace: 'nowrap', width: '5%' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                                            <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                                        </TableCell>
                                    </TableRow>
                                ) : users.length > 0 ? (
                                    users.map((user, idx) => (
                                        <TableRow key={user.id} sx={{
                                            transition: 'background-color 0.2s',
                                            '& td': { borderBottom: '1px solid #F0F0F0' },
                                            '&:last-child td': { borderBottom: 0 },
                                        }}>
                                            <TableCell sx={{ py: 1.25, px: 1, fontSize: '0.75rem', color: '#64748B', textAlign: 'center' }}>{(page - 1) * rowsPerPage + idx + 1}</TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.25, maxWidth: 140 }}>
                                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={user.name}>
                                                    {user.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.25, maxWidth: 170 }}>
                                                <Typography sx={{ fontSize: '0.75rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={user.email}>
                                                    {user.email}
                                                </Typography>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.25, whiteSpace: 'nowrap' }}>
                                                <Box sx={{
                                                    display: 'inline-block',
                                                    px: 1.25, py: 0.35, borderRadius: '4px',
                                                    bgcolor: '#E3F2FD', color: '#1976D2', fontSize: '0.7rem', fontWeight: 500
                                                }}>
                                                    {getRoleName(user.roleId)}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.25, whiteSpace: 'nowrap' }}>
                                                <Box
                                                    sx={{
                                                        display: 'inline-block',
                                                        px: 1.25, py: 0.35,
                                                        borderRadius: '4px',
                                                        bgcolor: user.isActive ? '#E8F5E8' : '#FFEBEE',
                                                        color: user.isActive ? '#2E7D32' : '#C62828',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {user.isActive ? 'Active' : 'Inactive'}
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.25, maxWidth: 110 }}>
                                                {user.cuName ? (
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={user.cuName}>
                                                        {user.cuName}
                                                        {user.cuType ? (
                                                            <Typography component="span" sx={{ fontSize: '0.7rem', color: '#888', ml: 0.5 }}>
                                                                ({user.cuType})
                                                            </Typography>
                                                        ) : null}
                                                    </Typography>
                                                ) : <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>-</Typography>}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.25, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(user.createdAt)}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.25, maxWidth: 110 }}>
                                                {user.uuName ? (
                                                    <Typography sx={{ fontSize: '0.75rem', color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={user.uuName}>
                                                        {user.uuName}
                                                        {user.uuType ? (
                                                            <Typography component="span" sx={{ fontSize: '0.7rem', color: '#888', ml: 0.5 }}>
                                                                ({user.uuType})
                                                            </Typography>
                                                        ) : null}
                                                    </Typography>
                                                ) : <Typography sx={{ fontSize: '0.75rem', color: '#64748B' }}>-</Typography>}
                                            </TableCell>
                                            <TableCell sx={{ py: 1.25, px: 1.25, fontSize: '0.75rem', color: '#64748B', whiteSpace: 'nowrap' }}>
                                                {formatDate(user.updatedAt)}
                                            </TableCell>
                                            <TableCell align="right" sx={{ py: 1.25, px: 1.5, whiteSpace: 'nowrap' }}>
                                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                    <PermissionWrapper permission="edit" mode="disable">
                                                        <Tooltip title="Edit">
                                                            <IconButton size="small" sx={{ color: '#FFBD65' }} onClick={() => handleOpenModal(user)}>
                                                                <SquarePen size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                    <PermissionWrapper permission="delete" mode="disable">
                                                        <Tooltip title="Delete">
                                                            <IconButton size="small" sx={{ color: '#FF4D49' }} onClick={() => handleDeleteClick(user.id)}>
                                                                <Trash2 size={16} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </PermissionWrapper>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={10} align="center" sx={{ py: 3, color: '#AAA', fontSize: '0.8rem' }}>
                                            {searchTerm ? 'No admin users found matching your search.' : 'No admin users found.'}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3, gap: 2, borderTop: '1px solid #F0F0F0' }}>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.78rem' }}>
                                Showing {users.length > 0 ? (page - 1) * rowsPerPage + 1 : 0} to {Math.min(page * rowsPerPage, totalItems)} of {totalItems} entries
                            </Typography>
                            <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" shape="rounded"
                                sx={{
                                    '& .MuiPaginationItem-root': {
                                        borderRadius: '6px',
                                        fontSize: '0.78rem'
                                    },
                                    '& .Mui-selected': {
                                        bgcolor: '#FFBD65 !important',
                                        color: '#fff',
                                    },
                                }}
                            />
                        </Box>
                    )}
                </Paper>

                {/* Add/Edit Modal */}
                <Dialog open={openModal} onClose={handleCloseModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '6px' } }}>
                    <DialogTitle sx={{ fontWeight: 600, color: '#222', pb: 1, fontSize: '1.1rem' }}>
                        {isEdit ? 'Edit Admin User' : 'Add New Admin User'}
                    </DialogTitle>
                    <DialogContent>
                        {modalLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
                                <CircularProgress size={24} sx={{ color: '#FFBD65' }} />
                            </Box>
                        ) : (
                            <Stack spacing={2} sx={{ mt: 1 }}>
                                {modalError && (
                                    <Alert severity="error" onClose={() => setModalError(null)} sx={{ fontSize: '0.8rem' }}>
                                        {modalError}
                                    </Alert>
                                )}
                                <TextField
                                    size='small'
                                    fullWidth
                                    label="Name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    autoComplete="off"
                                    inputProps={{ maxLength: 100 }}
                                    error={Boolean(fieldErrors.name)}
                                    helperText={fieldErrors.name}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />

                                <TextField
                                    size='small'
                                    fullWidth
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    autoComplete="off"
                                    error={Boolean(fieldErrors.email)}
                                    helperText={fieldErrors.email}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                />

                                <TextField
                                    size='small'
                                    fullWidth
                                    label="Password"
                                    name="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={handleInputChange}
                                    required={!isEdit}
                                    autoComplete="new-password"
                                    error={Boolean(fieldErrors.password)}
                                    helperText={fieldErrors.password}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                >
                                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <FormControl size='small' fullWidth error={Boolean(fieldErrors.roleId)}>
                                    <InputLabel sx={{ fontSize: '0.85rem' }}>Role</InputLabel>
                                    <Select
                                        name="roleId"
                                        value={formData.roleId}
                                        onChange={handleInputChange}
                                        label="Role"
                                        required
                                        sx={{
                                            borderRadius: '6px',
                                            fontSize: '0.85rem',
                                            '& fieldset': { borderColor: '#E0E0E0' },
                                            '&:hover fieldset': { borderColor: '#BDBDBD' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65', borderWidth: '1px' }
                                        }}
                                    >
                                        {roles.map((role) => (
                                            <MenuItem key={role.id} value={role.id} sx={{ fontSize: '0.85rem' }}>
                                                {role.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {fieldErrors.roleId && (
                                        <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1, fontSize: '0.75rem' }}>
                                            {fieldErrors.roleId}
                                        </Typography>
                                    )}
                                </FormControl>

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.isActive}
                                            onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                                            sx={{
                                                '& .MuiSwitch-switchBase.Mui-checked': {
                                                    color: '#FFBD65',
                                                },
                                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                    backgroundColor: '#FFBD65',
                                                },
                                            }}
                                        />
                                    }
                                    label={<Typography sx={{ fontSize: '0.85rem' }}>{formData.isActive ? "Active" : "Inactive"}</Typography>}
                                    sx={{ mt: 1 }}
                                />

                                {isEdit && (
                                    <Box sx={{ mt: 1, pt: 2, borderTop: '1px solid #EAEAEA' }}>
                                        <Typography
                                            sx={{
                                                fontSize: '0.85rem',
                                                fontWeight: 600,
                                                color: '#475569',
                                                mb: 2,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}
                                        >
                                            Audit Information
                                            <Typography component="span" sx={{ fontSize: '0.75rem', color: '#94A3B8', fontWeight: 400 }}>
                                                (Non-editable)
                                            </Typography>
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <TextField
                                                    size='small'
                                                    fullWidth
                                                    label="Created By"
                                                    value={auditInfo.cuName ? `${auditInfo.cuName}${auditInfo.cuType ? ` (${auditInfo.cuType})` : ''}` : '-'}
                                                    disabled
                                                    variant="outlined"
                                                    sx={{
                                                        '& .MuiInputBase-input.Mui-disabled': {
                                                            WebkitTextFillColor: '#475569',
                                                        },
                                                        bgcolor: '#F8FAFC'
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <TextField
                                                    size='small'
                                                    fullWidth
                                                    label="Created At"
                                                    value={formatDateTime(auditInfo.createdAt)}
                                                    disabled
                                                    variant="outlined"
                                                    sx={{
                                                        '& .MuiInputBase-input.Mui-disabled': {
                                                            WebkitTextFillColor: '#475569',
                                                        },
                                                        bgcolor: '#F8FAFC'
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <TextField
                                                    size='small'
                                                    fullWidth
                                                    label="Updated By"
                                                    value={auditInfo.uuName ? `${auditInfo.uuName}${auditInfo.uuType ? ` (${auditInfo.uuType})` : ''}` : '-'}
                                                    disabled
                                                    variant="outlined"
                                                    sx={{
                                                        '& .MuiInputBase-input.Mui-disabled': {
                                                            WebkitTextFillColor: '#475569',
                                                        },
                                                        bgcolor: '#F8FAFC'
                                                    }}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 12, sm: 6 }}>
                                                <TextField
                                                    size='small'
                                                    fullWidth
                                                    label="Updated At"
                                                    value={formatDateTime(auditInfo.updatedAt)}
                                                    disabled
                                                    variant="outlined"
                                                    sx={{
                                                        '& .MuiInputBase-input.Mui-disabled': {
                                                            WebkitTextFillColor: '#475569',
                                                        },
                                                        bgcolor: '#F8FAFC'
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5, pt: 1 }}>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple onClick={handleCloseModal} sx={{ color: '#222', textTransform: 'none', fontSize: '0.85rem' }}>
                            Cancel
                        </Button>
                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                            onClick={handleSubmit} disabled={submitting} variant="contained"
                            sx={{
                                bgcolor: '#FFBD65',
                                borderRadius: '6px',
                                textTransform: 'none',
                                px: 3,
                                color: '#fff',
                                fontSize: '0.85rem'
                            }}
                        >
                            {submitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? 'Update' : 'Create')}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Delete Modal */}
                <DeleteModal
                    open={deleteModalOpen}
                    onClose={() => setDeleteModalOpen(false)}
                    onDelete={handleConfirmDelete}
                    title="Delete Admin User"
                    description="Are you sure you want to delete this admin user? This action cannot be undone."
                    error={deleteError}
                    loading={isDeleting}
                />
            </Box>
        </PermissionWrapper>
    );
};

export default AdminUsers;